// AI Image Generator Service
class ImageGeneratorService {
  constructor() {
    // Using Unsplash API as a free alternative for now
    // In production, you would use services like DALL-E, Midjourney, or Stable Diffusion
    this.unsplashAccessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY || 'your-unsplash-key';
    this.baseUrl = 'https://api.unsplash.com';
  }

  // Generate a cartoon-style image based on thought content
  async generateCartoonImage(thought, author) {
    try {
      // Create search query based on thought content
      const searchQuery = this.createSearchQuery(thought, author);
      
      // For now, we'll use Unsplash to get relevant images
      // In production, you would integrate with AI image generation APIs
      const imageUrl = await this.getUnsplashImage(searchQuery);
      
      return {
        success: true,
        imageUrl: imageUrl,
        searchQuery: searchQuery
      };
    } catch (error) {
      console.error('Error generating image:', error);
      return {
        success: false,
        error: error.message || 'Failed to generate image',
        fallbackImage: this.getFallbackImage()
      };
    }
  }

  // Create a search query based on thought content
  createSearchQuery(thought, author) {
    // Extract keywords from thought
    const keywords = this.extractKeywords(thought);
    
    // Add cartoon-related terms with randomization
    const cartoonTerms = ['cartoon', 'illustration', 'drawing', 'art', 'motivational', 'inspirational', 'creative', 'abstract', 'minimalist'];
    const motivationalTerms = ['success', 'growth', 'mindset', 'positive', 'energy', 'focus', 'determination', 'achievement'];
    
    // Randomly select terms to add variety
    const randomCartoonTerms = cartoonTerms.sort(() => 0.5 - Math.random()).slice(0, 2);
    const randomMotivationalTerms = motivationalTerms.sort(() => 0.5 - Math.random()).slice(0, 1);
    
    // Combine keywords with random terms
    const searchTerms = [...keywords, ...randomCartoonTerms, ...randomMotivationalTerms].slice(0, 4);
    
    return searchTerms.join(' ');
  }

  // Extract keywords from thought text
  extractKeywords(thought) {
    const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them'];
    
    return thought
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 2 && !commonWords.includes(word))
      .slice(0, 5);
  }

  // Get image from Unsplash (free alternative)
  async getUnsplashImage(query) {
    try {
      // Check if API key is available
      if (!this.unsplashAccessKey || this.unsplashAccessKey === 'your-unsplash-key') {
        console.warn('Unsplash API key not configured, using fallback image');
        return this.getFallbackImage();
      }

      // Add random seed to ensure different images on each call
      const randomSeed = Math.random().toString(36).substring(7);
      const response = await fetch(
        `${this.baseUrl}/photos/random?query=${encodeURIComponent(query)}&orientation=landscape&w=800&h=600&seed=${randomSeed}`,
        {
          headers: {
            'Authorization': `Client-ID ${this.unsplashAccessKey}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch image from Unsplash');
      }

      const data = await response.json();
      return data.urls.regular;
    } catch (error) {
      console.error('Unsplash API error:', error);
      // Return fallback image instead of throwing error
      return this.getFallbackImage();
    }
  }

  // Get fallback image when generation fails
  getFallbackImage() {
    // Return a placeholder image or default motivational image
    // Using a more reliable placeholder service with better randomization
    const randomId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    return `https://picsum.photos/seed/${randomId}/800/600`;
  }

  // Create image with text overlay (client-side)
  async createImageWithOverlay(imageUrl, text, author) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Set canvas dimensions
        canvas.width = 800;
        canvas.height = 600;
        
        // Draw the background image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Add dark overlay at bottom for text readability
        const gradient = ctx.createLinearGradient(0, canvas.height - 150, 0, canvas.height);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, canvas.height - 150, canvas.width, 150);
        
        // Add text overlay
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Main thought text
        ctx.font = 'bold 24px Arial, sans-serif';
        const maxWidth = canvas.width - 40;
        const words = text.split(' ');
        let line = '';
        let y = canvas.height - 100;
        
        for (let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = ctx.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            ctx.fillText(line, canvas.width / 2, y);
            line = words[n] + ' ';
            y += 30;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line, canvas.width / 2, y);
        
        // Author text
        if (author) {
          ctx.font = 'italic 18px Arial, sans-serif';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.fillText(`— ${author}`, canvas.width / 2, y + 40);
        }
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          resolve(url);
        }, 'image/jpeg', 0.9);
      };
      
      img.onerror = () => {
        resolve(this.getFallbackImage());
      };
      
      img.src = imageUrl;
    });
  }

  // Generate image with overlay for a thought
  async generateThoughtImage(thought, author) {
    try {
      // Add a timestamp to ensure uniqueness
      const timestamp = Date.now();
      const uniqueThought = `${thought} ${timestamp}`;
      
      // First generate the base image
      const imageResult = await this.generateCartoonImage(uniqueThought, author);
      
      if (!imageResult.success) {
        return imageResult;
      }
      
      // Create overlay with text
      const imageWithOverlay = await this.createImageWithOverlay(
        imageResult.imageUrl,
        thought, // Use original thought for display
        author
      );
      
      return {
        success: true,
        imageUrl: imageWithOverlay,
        originalImageUrl: imageResult.imageUrl,
        searchQuery: imageResult.searchQuery
      };
    } catch (error) {
      console.error('Error generating thought image:', error);
      return {
        success: false,
        error: error.message,
        fallbackImage: this.getFallbackImage()
      };
    }
  }
}

// Create singleton instance
const imageGeneratorService = new ImageGeneratorService();

export default imageGeneratorService;

